<script setup lang="ts">
import { defineAsyncComponent } from 'vue';

import type { OrderInsurance } from '~/types/ecommerce/generated.codegen';

const props = defineProps<{
  insurances: OrderInsurance[];
}>();

const OrdersInsuranceCoverageModal = defineAsyncComponent(
  () => import('~/components/orders/insurance-coverage-modal/index.vue'),
);

const coverageModalOpen = ref(false);

const titleText = computed(() => {
  if (props.insurances.length !== 1) return 'Seguro Risco Diversos';
  const years = props.insurances[0].coverage_years;
  if (years == null) return 'Seguro Risco Diversos';
  return `Seguro Risco Diversos (${years} ano${years > 1 ? 's' : ''})`;
});
</script>

<template>
  <div class="order-insurance-kit-card">
    <div class="order-insurance-kit-card__header">
      <p class="order-insurance-kit-card__title">
        {{ titleText }}
      </p>
      <SolTag
        id="order-insurance-kit-tag-added"
        class="order-insurance-kit-card__tag"
        size="small"
        type="positive">
        <icon-girassol:check
          class="icon-size-small text-feedback-positive-pure" />
        Adicionado
      </SolTag>
    </div>
    <p class="order-insurance-kit-card__description">
      Proteção contra vendaval, tempestade, incêndios, granizo, roubo, furto,
      danos elétricos, inundações e alagamentos para o kit de energia solar
      instalado.
    </p>

    <div class="order-insurance-kit-card__footer">
      <button
        type="button"
        class="order-insurance-kit-card__link"
        @click="coverageModalOpen = true">
        <span>Detalhes do que este seguro cobre</span>
        <icon-girassol:open-in-new class="order-insurance-kit-card__link-icon" />
      </button>
    </div>

    <OrdersInsuranceCoverageModal
      :open="coverageModalOpen"
      @close="coverageModalOpen = false"
      @primary-action="coverageModalOpen = false" />
  </div>
</template>

<style lang="scss" scoped>
.order-insurance-kit-card {
  @apply flex flex-col w-full min-w-0;
  @apply rounded-lg border border-neutral-high-medium;
  @apply bg-neutral-high-light p-4;

  &__header {
    @apply flex flex-wrap items-center gap-3;
  }

  &__title {
    @apply fonts-body-medium-bold text-neutral-low-medium m-0;
  }

  &__tag {
    flex-shrink: 0;
    @apply gap-quark;
  }

  &__description {
    @apply fonts-body-medium-regular text-neutral-low-light m-0 mt-3;
  }

  &__footer {
    @apply flex flex-col gap-2 mt-5;
  }

  &__link {
    @apply flex items-center gap-quark p-0 m-0;
    width: fit-content;
    max-width: 100%;
    @apply fonts-body-medium-regular text-neutral-low-dark;
    @apply bg-transparent border-none cursor-pointer text-left underline;

    &:focus-visible {
      @apply rounded-sm ring-2 ring-feedback-informative-medium ring-offset-2;
    }
  }

  &__link-icon {
    @apply icon-size-small flex-shrink-0;
  }
}
</style>
